import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useDebounceCallback } from '@react-hook/debounce';
import InputBase from '@material-ui/core/InputBase';
import { withStyles } from '@material-ui/core';
import {
  getDropdownStyles,
  getMenuProps,
  getArrowClasses,
  getInputClasses,
  styles
} from '../Dropdown/common-styles';
import usePrevious from 'components/hooks/usePrevious';
import MenusWrapper from './components/MenusWrapper';
import Menu from './components/Menu';
import {
  flattenTree,
  renderSelectedDefault,
  renderSelectedNotFoundDefault,
  getSelectedOptions
} from './MultiLevelDropdown.utils';
import { StyledSelect } from './MultiLevelDropdown.style';

/**
 * MultiLevelDropdown props
 * TODO: document other properties
 * @typedef {object} MultiLevelDropdownProps
 * @property {boolean} disabledMode Is the component greyed out
 */

/**
 * Team picker component
 * @param {MultiLevelDropdownProps} props
 * @returns {JSX.Element}
 */

const MultiLevelDropdown = props => {
  const {
    disabledMode,
    disabledDropdown,
    tree,
    selected,
    disabled,
    dataAttrName,
    size = 'medium',
    width,
    height,
    menuWidth = '30rem',
    menuWidthExpanded = '60rem',
    menuHeight = '30rem',
    numOfMenus = 3,
    classes,
    inputProps,
    multiple,
    multipleLimit,
    searchable,
    searchPlaceholder,
    isDisableChildrenOfSelectedParent,
    renderSelected = renderSelectedDefault,
    renderSelectedNotFound = renderSelectedNotFoundDefault,
    onOpen = null,
    onClose = null,
    onChange,
    customAllItem = {},
    optionActions,
    noSearchResultsAction,
    isOpenParentsOfSelected,
    isInline
  } = props;
  const previousTree = usePrevious(tree);
  const [options, setOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openedParents, setOpenedParents] = useState([]);

  const flatTree = flattenTree(tree, 'children');
  const selectedOptions = getSelectedOptions(flatTree, selected);
  const selectedPathIds = _.keyBy(
    selectedOptions.flatMap(({ path }) => path),
    'id'
  );
  const isMaxLimit =
    multiple && multipleLimit && _.size(selected) === multipleLimit;

  useEffect(() => {
    if (
      !_.isEqual(
        previousTree ? previousTree.map(({ value }) => value) : [],
        tree.map(({ value }) => value)
      )
    ) {
      setOptions(tree);
    }
  }, [tree]);

  useEffect(() => {
    if (isOpen && isOpenParentsOfSelected) {
      const selectedParentsIds = Object.keys(selectedPathIds).map(Number);
      const selectedParentsObjects = flatTree.filter(({ value }) =>
        selectedParentsIds.includes(value)
      );

      setTimeout(() => {
        const menuElement = document.getElementById(
          `${selected[0]}-picker-item`
        );

        if (menuElement) {
          menuElement.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 200);

      setOpenedParents(selectedParentsObjects);
    }
  }, [selected, isOpen]);

  useEffect(() => {
    onSearchValueChange();
  }, [searchValue]);

  const handleClose = () => {
    onClose && onClose();
    setSearchValue('');
    setIsOpen(false);
    setOpenedParents([]);
  };
  const handleOpen = () => (onOpen ? onOpen() : setIsOpen(true));
  const handleOptionClick = option => {
    const { value, children = [] } = option;

    const isCustomAllOptionSelected = value === customAllItem.id;

    let selectedIds = [];

    if (isCustomAllOptionSelected) {
      selectedIds = [value];
    } else {
      const { value } = option;

      if (multiple) {
        const isParent = children.length > 0;

        // if isDisableChildrenOfSelectedParent - need to filter out all children of selected parent and return only parent
        if (isDisableChildrenOfSelectedParent && isParent) {
          const childrenOfSelectedParent = flattenTree(
            children,
            'children'
          ).map(({ value }) => value);
          const selectedItems =
            selected.filter(
              item =>
                !childrenOfSelectedParent.includes(item) &&
                item !== customAllItem.id
            ) || [];
          selectedIds = _.xorBy(selectedItems, [value]);
        } else {
          const selectedItems =
            selected.filter(item => item !== customAllItem.id) || [];
          selectedIds = _.xorBy(selectedItems, [value]);
        }
      } else {
        selectedIds = [value];
      }
    }
    onChange(selectedIds);
    if (!multiple) {
      setIsOpen(false);
      setSearchValue('');
      setOpenedParents([]);
    }
  };
  const handleOpenMenuClick = (option, level) => {
    const newOpenedParents = openedParents.slice(0, level).concat(option);
    setOpenedParents(newOpenedParents);
    setTimeout(() => {
      const menuElement = document.getElementById(
        `multiLevelMenu-${level + 1}`
      );
      if (menuElement) {
        menuElement.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const onSearch = value => setSearchValue(value);
  const onSearchValueChange = useDebounceCallback(async () => {
    if (searchValue === '') {
      setOptions(tree);
      setIsSearchMode(false);
    } else {
      setOpenedParents([]);
      const filteredOptions = flatTree.filter(({ keyword = '' }) => {
        return keyword.toLowerCase().includes(searchValue.toLowerCase());
      });
      setOptions(filteredOptions);
      setIsSearchMode(true);
    }
  }, 700);
  const handleCloseMenus = () => {
    setIsOpen(false);
    setOpenedParents([]);
  };
  const handleClearSearch = () => {
    setSearchValue('');
  };

  const selectedIdsNotInTree = _.difference(
    selected,
    flatTree.map(({ value }) => value)
  );

  const menusWrapper = (
    <MenusWrapper
      menuWidth={menuWidth}
      menuWidthExpanded={menuWidthExpanded}
      menuHeight={menuHeight}
      numOfMenus={numOfMenus}
      isInline={isInline}
      isSearchMode={isSearchMode}
    >
      <Menu
        options={options}
        selected={selected}
        disabled={disabled}
        selectedPathIds={selectedPathIds}
        dataAttrName={dataAttrName}
        menuWidth={menuWidth}
        menuWidthExpanded={menuWidthExpanded}
        level={0}
        multiple={multiple}
        isMaxLimit={isMaxLimit}
        isSearchMode={isSearchMode}
        onOptionClick={option => handleOptionClick(option)}
        onOpenMenuClick={option => handleOpenMenuClick(option, 0)}
        onSearch={onSearch}
        searchable={searchable}
        searchValue={searchValue}
        classes={classes}
        searchPlaceholder={searchPlaceholder}
        openedParents={openedParents}
        noSearchResultsAction={noSearchResultsAction}
        optionActions={optionActions}
        handleCloseMenus={handleCloseMenus}
        handleClearSearch={handleClearSearch}
        isDisableChildrenOfSelectedParent={isDisableChildrenOfSelectedParent}
      />
      {openedParents.map((openedParent, index) => {
        return (
          <Menu
            isSubMenu
            key={index}
            options={openedParent.children}
            selected={selected}
            disabled={disabled}
            selectedPathIds={selectedPathIds}
            dataAttrName={dataAttrName}
            menuWidth={menuWidth}
            menuWidthExpanded={menuWidthExpanded}
            level={index + 1}
            multiple={multiple}
            isMaxLimit={isMaxLimit}
            isSearchMode={isSearchMode}
            onOptionClick={option => handleOptionClick(option)}
            onOpenMenuClick={option => handleOpenMenuClick(option, index + 1)}
            classes={classes}
            searchPlaceholder={searchPlaceholder}
            openedParents={openedParents}
            optionActions={optionActions}
            handleCloseMenus={handleCloseMenus}
            handleClearSearch={handleClearSearch}
            isDisableChildrenOfSelectedParent={
              isDisableChildrenOfSelectedParent
            }
          />
        );
      })}
    </MenusWrapper>
  );

  if (isInline) {
    return menusWrapper;
  }

  return (
    <StyledSelect
      id='select'
      style={{ ...getDropdownStyles({ width, height: height || size.height }) }}
      data-cy={`${dataAttrName}-picker`}
      value={selected}
      displayEmpty={true}
      disabled={disabledDropdown || disabledMode}
      disabledMode={disabledMode}
      renderValue={() =>
        // check if there are selectedIds that are not in tree
        selectedIdsNotInTree.length > 0
          ? renderSelectedNotFound(selected, selectedIdsNotInTree)
          : renderSelected(selectedOptions)
      }
      IconComponent={() =>
        getArrowClasses({ isOpen, classes, disabled: false, size })
      }
      input={
        <InputBase
          classes={{
            ...getInputClasses(null, classes, isOpen, inputProps, false),
            focused: classes.focused
          }}
        />
      }
      MenuProps={{
        ...getMenuProps({ menuHeight, padding: '0rem' })
      }}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
    >
      {menusWrapper}
    </StyledSelect>
  );
};

export default withStyles(styles)(MultiLevelDropdown);
